/**
 * Čisté řazení a hledání pro DataTable. Bez Reactu, ať jdou spustit testem.
 */

export type SortDir = 'asc' | 'desc';

export interface SortState {
  /** Klíč sloupce (Column.key). */
  key: string;
  dir: SortDir;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '' ||
    (typeof value === 'number' && Number.isNaN(value));
}

function comparable(value: unknown): number | string {
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();
  return String(value);
}

/**
 * Porovnání dvou hodnot vzestupně: čísla číselně, texty podle jazyka
 * (bez ohledu na velikost písmen a diakritiku, „A10“ za „A9“).
 */
export function compareValues(a: unknown, b: unknown): number {
  const x = comparable(a);
  const y = comparable(b);
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  return collator.compare(String(x), String(y));
}

/**
 * Seřazená kopie řádků. Prázdné hodnoty (null, undefined, '') jsou vždy
 * na konci, ať se řadí kterýmkoli směrem. Shodné hodnoty drží původní pořadí.
 */
export function sortRows<T>(
  rows: readonly T[],
  sort: SortState | null | undefined,
  value: (row: T) => unknown,
): T[] {
  if (!sort) return [...rows];
  const sign = sort.dir === 'desc' ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index, v: value(row) }))
    .sort((a, b) => {
      const ea = isEmpty(a.v);
      const eb = isEmpty(b.v);
      if (ea || eb) return ea === eb ? a.index - b.index : ea ? 1 : -1;
      return sign * compareValues(a.v, b.v) || a.index - b.index;
    })
    .map((item) => item.row);
}

/** Klik na hlavičku: jiný sloupec začne vzestupně, stejný sloupec otočí směr. */
export function nextSort(current: SortState | null | undefined, key: string): SortState {
  if (current?.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: 'asc' };
}

/** Hodnota pro atribut aria-sort na hlavičce sloupce. */
export function ariaSort(
  sort: SortState | null | undefined,
  key: string,
): 'ascending' | 'descending' | 'none' {
  if (sort?.key !== key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}

/** Text pro hledání: malá písmena, bez diakritiky („Říha“ najde i „riha“). */
export function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Řádky, jejichž text obsahuje všechna slova dotazu. Prázdný dotaz vrátí vše. */
export function filterRows<T>(
  rows: readonly T[],
  query: string,
  text: (row: T) => string,
): T[] {
  const words = normalizeSearch(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [...rows];
  return rows.filter((row) => {
    const haystack = normalizeSearch(text(row));
    return words.every((word) => haystack.includes(word));
  });
}

/** Výchozí text řádku pro hledání: textové a číselné hodnoty zadaných klíčů. */
export function rowText(row: Record<string, unknown>, keys: readonly string[]): string {
  return keys
    .map((key) => row[key])
    .filter((value) => typeof value === 'string' || typeof value === 'number')
    .join(' ');
}
